/**
 * Appends query parameters to a given URL.
 * @param baseUrl The base URL string.
 * @param params An object of query parameters to append.
 * @returns The new URL string with appended parameters.
 */

// Example:
// const newApiUrl = appendQueryParams('https://api.my-app.com/v1/users', {
//   page: 2,
//   sortBy: 'createdAt'
// });
// console.log(newApiUrl); // https://api.my-app.com/v1/users?page=2&sortBy=createdAt
export function appendQueryParams(baseUrl: string, params: Record<string, any>): string {
  try {
    const url = new URL(baseUrl);

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        return; // Skip null/undefined
      }

      if (Array.isArray(value)) {
        // If it's an array, add each value separately
        value.forEach((item) => url.searchParams.append(key, String(item)));
      } else {
        // Otherwise, just append the stringified value
        url.searchParams.append(key, String(value));
      }
    });

    return url.toString();
  } catch (error) {
    console.error("Invalid URL or params:", error);
    return baseUrl; // Return original URL on error
  }
}

// This should be identical to frontend_app_config in application_helper.rb
type AppConfig = {
  application_fqdn: {
    host?: string;
    protocol?: string;
    port?: number | string;
  };
};

declare global {
  interface Window {
    __APP_CONFIG__?: AppConfig;
  }
}

const absoluteUrlPattern = /^[a-z][a-z0-9+\-.]*:\/\//i;

function configuredAppOrigin() {
  if (typeof window === "undefined") {
    return undefined;
  }
  const appFqdn = window.__APP_CONFIG__?.application_fqdn;
  if (!appFqdn?.host) {
    return undefined;
  }
  const protocol = "https";
  const port = typeof appFqdn.port === "number" || typeof appFqdn.port === "string" ? String(appFqdn.port) : "";
  const portSegment = port ? `:${port}` : "";
  return `${protocol}://${appFqdn.host}${portSegment}`;
}

// Fill the full FQDN of the app - for routes like /api/v1/.. etc., return https://jalories.com/api/v1/..
// will use appropriate development/production values
export function withAppDomain(baseRoute: string) {
  if (absoluteUrlPattern.test(baseRoute)) {
    return baseRoute;
  }

  if (typeof window === "undefined") {
    return baseRoute;
  }

  const origin = configuredAppOrigin() || window.location.origin;

  try {
    return new URL(baseRoute, origin).toString();
  } catch (error) {
    console.error("Failed to resolve app domain for route:", error);
    return baseRoute;
  }
}
